import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CompactionEfficiencySummary } from "../../components/CompactionEfficiencySummary";
import { CompactionPairList } from "../../components/CompactionPairList";
import { ActivityCompactionMetricIcon } from "../../components/ActivityDetailMetricIcons";
import { SummaryMetric } from "./components/ActivityDetailSummaryMetric";
import { useCompactionExclusions } from "./hooks/useCompactionExclusions";
import { getCompactionGain } from "./utils/activityDetailAnalysis";

const SectionHeader = ({ title, children }) => (
  <div className="char-section-header char-section-header--general-info flex flex-row flex-wrap gap-3 !justify-start items-center !mb-0 min-w-0">
    <h2 className="char-section-header-title">{title}</h2>
    {children}
  </div>
);

/**
 * Builds newest-first before/after compaction pairs from historyCycle
 * (which is itself ordered newest-first). For an entry marked `packmat`,
 * the "before" state is the chronologically previous reading, i.e. the
 * next entry in this descending array (idx + 1).
 */
const buildCompactionPairs = (historyCycle) => {
  if (!historyCycle?.length) return [];

  const events = historyCycle
    .map((item, idx) => ({ item, idx }))
    .filter(({ item, idx }) => item?.packmat && idx + 1 < historyCycle.length);

  return events.map(({ item, idx }) => ({
    after: item,
    before: historyCycle[idx + 1],
    gain: item.diff_filling,
  }));
};

// Numbers pairs chronologically (oldest = 1) over the full merged list
// (server-confirmed + locally excluded), so numbering stays stable
// regardless of exclusion state.
const withOrder = (pairs) => {
  const sorted = [...pairs].sort(
    (a, b) => new Date(b.after._time) - new Date(a.after._time),
  );
  return sorted.map((pair, index) => ({
    ...pair,
    order: sorted.length - index,
  }));
};

const ActivityCompactionTab = ({
  container,
  historyCycle,
  numberOfPackmat,
  onAfterUpdate,
}) => {
  const { t } = useTranslation();
  const compactionGain = getCompactionGain(historyCycle);

  const serverPairs = useMemo(
    () => buildCompactionPairs(historyCycle),
    [historyCycle],
  );

  const {
    excludedPairs,
    setPairExcluded,
    undo,
    canUndo,
    pendingKey,
    errorKey,
  } = useCompactionExclusions({
    onAfterChange: onAfterUpdate,
  });

  const displayedPairs = useMemo(() => {
    const excludedKeys = new Set(excludedPairs.map((pair) => pair.after._time));
    const included = serverPairs
      .filter((pair) => !excludedKeys.has(pair.after._time))
      .map((pair) => ({ ...pair, excluded: false }));
    const excluded = excludedPairs.map((pair) => ({
      ...pair,
      excluded: true,
    }));
    return withOrder([...included, ...excluded]);
  }, [serverPairs, excludedPairs]);

  const validPairs = useMemo(
    () => displayedPairs.filter((pair) => !pair.excluded),
    [displayedPairs],
  );

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="grid grid-cols-4 md:grid-cols-2 sm:grid-cols-1 gap-3">
        <SummaryMetric
          label={t("Compaction gain")}
          value={
            container?.packmatActive && compactionGain != null
              ? `${compactionGain}%`
              : "-"
          }
          detail={
            container?.packmatActive
              ? t("compactionCount", { count: numberOfPackmat || 0 })
              : t("Compaction inactive")
          }
          icon={<ActivityCompactionMetricIcon />}
          inlineDetail
        />
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-1 w-full">
        <div className="flex flex-col min-w-0">
          <SectionHeader title={t("Compaction efficiency evolution")}>
            <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium bg-[#E6F7F5] text-[#00A7A6] whitespace-nowrap">
              {t("compactionCount", { count: validPairs.length })}
            </span>
          </SectionHeader>
          <article className="mt-3 bg-white border border-gray-300 rounded-xl w-full min-w-0 flex flex-col px-2 py-2">
            <CompactionEfficiencySummary compactionPairs={validPairs} />
          </article>
        </div>
      </div>

      <div className="flex flex-col min-w-0">
        <SectionHeader title={`${t("Before")} / ${t("After")}`}>
          <button
            type="button"
            onClick={undo}
            disabled={!canUndo}
            className="ml-auto inline-flex items-center gap-1.5 text-sm font-medium text-[#222222] hover:text-[#00A7A6] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <i className="fas fa-undo text-xs" aria-hidden></i>
            {t("Undo")}
          </button>
        </SectionHeader>
        <article className="mt-3 bg-white border border-gray-300 rounded-xl w-full min-w-0">
          <CompactionPairList
            container={container}
            history={historyCycle}
            pairs={displayedPairs}
            pendingKey={pendingKey}
            errorKey={errorKey}
            onToggleExcluded={setPairExcluded}
          />
        </article>
      </div>
    </div>
  );
};

export default ActivityCompactionTab;
